/**
 * Hook Firebase Auth — pour les pages publiques SmartLawyer
 * Complètement séparé du useAuth() Chainlit
 */

import { useState, useEffect, useCallback } from 'react';

import i18n from '@/i18n';
import {
  getAdditionalUserInfo,
} from 'firebase/auth';
import {
  onAuthChange, signInWithGoogle, signInWithMicrosoft,
  signInWithEmail, signUpWithEmail, signOutUser, getIdToken,
  type User,
} from '@/lib/firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SubStatus {
  plan:             string;
  has_subscription: boolean;
  interval:         string | null;
  period_end:       string | null;
  last_invoice_url: string | null;
}

interface PublicAuthState {
  user:       User | null;
  loading:    boolean;
  error:      string;
  subStatus:  SubStatus | null;
}

interface PublicAuthActions {
  loginWithGoogle:    () => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  loginWithEmail:     (email: string, password: string) => Promise<void>;
  registerWithEmail:  (email: string, password: string) => Promise<void>;
  logout:             () => Promise<void>;
  getToken:           () => Promise<string | null>;
  clearError:         () => void;
  refreshSubStatus:   () => Promise<void>;
}

// ─── Helper debug ─────────────────────────────────────────────────────────────

function logFirebaseUser(label: string, result: any, isNewUser: boolean | null = null) {
  const u = result.user;
  console.group(`🔥 [Firebase] ${label}`);
  console.log('uid              :', u.uid);
  console.log('email            :', u.email);
  console.log('displayName      :', u.displayName);
  console.log('photoURL         :', u.photoURL);
  console.log('emailVerified    :', u.emailVerified);
  console.log('phoneNumber      :', u.phoneNumber);
  console.log('providerId       :', u.providerId);
  console.log('providerData     :', u.providerData);
  console.log('metadata.creationTime    :', u.metadata?.creationTime);
  console.log('metadata.lastSignInTime  :', u.metadata?.lastSignInTime);
  if (isNewUser !== null) {
    console.log('isNewUser        :', isNewUser, isNewUser ? '👈 INSCRIPTION' : '👈 CONNEXION');
  }
  const addInfo = getAdditionalUserInfo(result);
  console.log('additionalUserInfo:', addInfo);
  console.groupEnd();
}

// ─── Helper — notifie le backend d'une nouvelle inscription ──────────────────

async function notifyNewUser(
  user: User,
  provider: 'google' | 'microsoft' | 'email',
): Promise<void> {
  try {
    console.log(`[usePublicAuth] notifyNewUser → provider=${provider} uid=${user.uid} email=${user.email}`);
    const token = await user.getIdToken();
    console.log(`[usePublicAuth] idToken obtenu (${token.length} chars)`);

    const res = await fetch('/api/auth/firebase/on-register', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ provider }),
    });

    const data = await res.json().catch(() => null);
    if (res.ok) {
      console.log(`[usePublicAuth] ✅ on-register réponse (${res.status}):`, data);
    } else {
      console.warn(`[usePublicAuth] ⚠️  on-register erreur (${res.status}):`, data);
    }
  } catch (err) {
    console.error('[usePublicAuth] ❌ notifyNewUser exception:', err);
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePublicAuth(): PublicAuthState & PublicAuthActions {
  const [user,      setUser]      = useState<User | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [subStatus, setSubStatus] = useState<SubStatus | null>(null);

  // ── Fetch statut abonnement ──────────────────────────────────────────────

  const fetchSubStatus = useCallback(async (currentUser: User) => {
    try {
      const token = await currentUser.getIdToken();
      const res   = await fetch('/api/checkout/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSubStatus(await res.json());
    } catch (err) {
      console.warn('[usePublicAuth] subStatus fetch failed:', err);
    }
  }, []);

  // ── Auth listener ─────────────────────────────────────────────────────────

  useEffect(() => {
    const unsubscribe = onAuthChange((u) => {
      console.log('[usePublicAuth] onAuthChange →', u ? `uid=${u.uid} email=${u.email}` : 'null (déconnecté)');
      setUser(u);
      setLoading(false);
      if (u) {
        fetchSubStatus(u);
      } else {
        setSubStatus(null);
      }
    });
    return unsubscribe;
  }, [fetchSubStatus]);

  // ── refreshSubStatus — appelable manuellement après paiement Stripe ──────

  const refreshSubStatus = useCallback(async () => {
    if (user) await fetchSubStatus(user);
  }, [user, fetchSubStatus]);

  const handleError = (e: any) => {
    console.error('[usePublicAuth] ❌ Firebase error:', e.code, e.message);
    // Messages traduits sous auth.firebase_errors.<code sans préfixe auth/>
    const key = `auth.firebase_errors.${String(e.code || '').replace(/^auth\//, '')}`;
    setError(
      i18n.exists(key)
        ? i18n.t(key)
        : e.message || i18n.t('auth.firebase_errors.default')
    );
  };

  // ── Google ────────────────────────────────────────────────────────────────

  const loginWithGoogle = useCallback(async () => {
    setError(''); setLoading(true);
    try {
      const result    = await signInWithGoogle();
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
      logFirebaseUser('Google sign-in', result, isNewUser);
      if (isNewUser) await notifyNewUser(result.user, 'google');
    } catch (e) { handleError(e); }
    finally { setLoading(false); }
  }, []);

  // ── Microsoft ─────────────────────────────────────────────────────────────

  const loginWithMicrosoft = useCallback(async () => {
    setError(''); setLoading(true);
    try {
      const result    = await signInWithMicrosoft();
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
      logFirebaseUser('Microsoft sign-in', result, isNewUser);
      if (isNewUser) await notifyNewUser(result.user, 'microsoft');
    } catch (e) { handleError(e); }
    finally { setLoading(false); }
  }, []);

  // ── Email login ───────────────────────────────────────────────────────────

  const loginWithEmail = useCallback(async (email: string, password: string) => {
    setError(''); setLoading(true);
    try {
      const result = await signInWithEmail(email, password);
      logFirebaseUser('Email login', result);
    }
    catch (e) { handleError(e); }
    finally { setLoading(false); }
  }, []);

  // ── Email register ────────────────────────────────────────────────────────

  const registerWithEmail = useCallback(async (email: string, password: string) => {
    setError(''); setLoading(true);
    try {
      const result = await signUpWithEmail(email, password);
      logFirebaseUser('Email register', result, true);
      await notifyNewUser(result.user, 'email');
    }
    catch (e) { handleError(e); }
    finally { setLoading(false); }
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────────

  const logout = useCallback(async () => {
    console.log('[usePublicAuth] logout');
    await signOutUser();
    setUser(null);
    setSubStatus(null);
  }, []);

  const getToken = useCallback(() => getIdToken(), []);

  return {
    user, loading, error, subStatus,
    loginWithGoogle, loginWithMicrosoft, loginWithEmail,
    registerWithEmail, logout, getToken,
    clearError:       () => setError(''),
    refreshSubStatus,
  };
}